/**
 * B709 leg 1 — the storefront Memberships page: 632:2 at 1440 (the tiers
 * compared in four columns, the LIMITED card, the 65-and-over and add-on
 * strips, what every plan shares, BEFORE YOU BUY) and 634:77 at 390 (one
 * radio per plan with its one-line summary). The shapes come from
 * lib/membershipsPage.ts; this file only lays them out. CHOOSE is shut with
 * its reason until the buyer's purchase door lands (B709 (ii)); the ZIP
 * banner and the gift toggle wait on the switches a buyer cannot read yet
 * (B709 (i)), so they are not drawn.
 */
import type { ReactElement } from "react";
import { Banner, Button, EmptyState, RadioGroup, SegmentedControl, Skeleton } from "@transact/ui";
import { MEMBERSHIP_SIGNUP_SHUT, type MembershipsOffer, type Term, type TierCard } from "../lib/membershipsPage.js";

export type MembershipsState =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "empty" }
  | { readonly kind: "ready"; readonly offer: MembershipsOffer };

const SHUT_ID = "mships-shut";

function Bar(props: { readonly businessName?: string | undefined; readonly logoUrl?: string | undefined; readonly ticketsUrl?: string | undefined }): ReactElement {
  return (
    <header className="mships-bar" data-redline="mships-bar">
      <p className="mships-brand">
        <span className="mships-brand-tile" aria-hidden="true">
          {props.logoUrl !== undefined ? <img src={props.logoUrl} alt="" /> : null}
        </span>
        {props.businessName ?? "Memberships"}
      </p>
      {props.ticketsUrl !== undefined ? (
        <a className="mships-back" href={props.ticketsUrl}>
          ‹ Tickets
        </a>
      ) : null}
    </header>
  );
}

function Lines(props: { readonly lines: readonly { readonly text: string; readonly has: boolean }[] }): ReactElement {
  return (
    <ul className="mships-gets">
      {props.lines.map((l) => (
        <li key={l.text} className={l.has ? "mships-line" : "mships-line mships-line--off"}>
          <span className="mships-mark" aria-hidden="true">
            {l.has ? "✓" : "–"}
          </span>
          {l.has ? l.text : <span>{l.text}<span className="visually-hidden"> — not included</span></span>}
        </li>
      ))}
    </ul>
  );
}

function Tier(props: { readonly tier: TierCard }): ReactElement {
  const t = props.tier;
  return (
    <article className="mships-tier" aria-label={t.name} data-redline={`mships-tier-${t.tierId}`}>
      <h3 className="mships-tier-name">{t.name}</h3>
      <p className="mships-price">
        <span className="mships-figure">{t.price}</span> <span className="mships-per">{t.per}</span>
      </p>
      {t.monthly !== undefined ? <p className="mships-note">{t.monthly}</p> : null}
      <h4 className="mships-cap">Who it covers</h4>
      <ul className="mships-covers">
        {t.covers.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <h4 className="mships-cap">What you get</h4>
      <Lines lines={t.gets} />
      <Button variant="neutral" disabled describedBy={SHUT_ID} onClick={() => undefined}>
        {`Choose ${t.name}`}
      </Button>
    </article>
  );
}

export function MembershipsPage(props: {
  readonly venue: { readonly businessName?: string | undefined; readonly logoUrl?: string | undefined };
  readonly state: MembershipsState;
  readonly term: Term;
  /** The phone list's picked plan. */
  readonly picked?: string | undefined;
  readonly onTerm: (term: Term) => void;
  readonly onPick: (tierId: string) => void;
  readonly onRetry: () => void;
  readonly ticketsUrl?: string | undefined;
}): ReactElement {
  const s = props.state;
  return (
    <main className="mships" aria-label="Memberships" aria-busy={s.kind === "loading" || undefined}>
      <Bar businessName={props.venue.businessName} logoUrl={props.venue.logoUrl} ticketsUrl={props.ticketsUrl} />
      <div className="mships-body">
        {s.kind === "loading" ? (
          <>
            <p className="mships-kicker">Memberships</p>
            <p className="visually-hidden" role="status">
              Loading the memberships…
            </p>
            <Skeleton lines={6} />
          </>
        ) : s.kind === "error" ? (
          <>
            <p className="mships-kicker">Memberships</p>
            <Banner tone="danger">
              {s.message}
              <Button variant="neutral" onClick={props.onRetry}>
                Try again
              </Button>
            </Banner>
          </>
        ) : s.kind === "empty" ? (
          <EmptyState
            title="No memberships to buy here yet"
            body="Memberships are not on sale online here yet — tickets are."
            action={props.ticketsUrl !== undefined ? <a href={props.ticketsUrl}>See tickets</a> : undefined}
          />
        ) : (
          <Offer offer={s.offer} term={props.term} picked={props.picked} onTerm={props.onTerm} onPick={props.onPick} />
        )}
      </div>
    </main>
  );
}

function Offer(props: { readonly offer: MembershipsOffer; readonly term: Term; readonly picked?: string | undefined; readonly onTerm: (t: Term) => void; readonly onPick: (tierId: string) => void }): ReactElement {
  const o = props.offer;
  const all = [...o.tiers, ...o.limited];
  return (
    <>
      <section className="mships-hero" data-redline="mships-hero">
        <div className="mships-hero-words">
          <p className="mships-kicker" data-redline="mships-kicker">
            Memberships
          </p>
          <h1 className="mships-title" data-redline="mships-title">
            {o.title}
          </h1>
          <p className="mships-sub" data-redline="mships-sub">
            {o.sub}
          </p>
        </div>
        {o.terms.length > 1 ? (
          <div className="mships-term" role="group" aria-label="Plan term" data-redline="mships-term">
            <SegmentedControl
              legend="Plan term"
              name="membership-term"
              options={[
                { value: "ANNUAL", label: "Pay yearly" },
                { value: "MONTHLY", label: "Pay monthly" },
              ]}
              value={props.term}
              onChange={(v) => props.onTerm(v === "MONTHLY" ? "MONTHLY" : "ANNUAL")}
            />
          </div>
        ) : null}
      </section>
      <section className="mships-plans" aria-labelledby="mships-plans-cap">
        <div className="mships-plans-head">
          <h2 className="mships-section-cap" id="mships-plans-cap">
            Choose a plan
          </h2>
          <p className="mships-note" id={SHUT_ID}>
            {MEMBERSHIP_SIGNUP_SHUT}
          </p>
        </div>
        <div className="mships-tiers" data-redline="mships-tiers">
          {o.tiers.map((t) => (
            <Tier key={t.tierId} tier={t} />
          ))}
        </div>
        {/* 634:77 — the phone's list: one radio per plan, its summary under the name. */}
        <div className="mships-list">
          <RadioGroup
            legend="Choose a plan"
            name="membership-plan"
            options={all.map((t) => ({ value: t.tierId, label: `${t.name} — ${t.price} ${t.per}`, hint: t.summary }))}
            value={props.picked ?? ""}
            onChange={props.onPick}
          />
        </div>
      </section>
      {o.limited.length > 0 || o.seniors !== undefined || o.addOns !== undefined ? (
        <section className="mships-variants" aria-label="More plans and add-ons">
          {o.limited.map((t) => (
            <article key={t.tierId} className="mships-card mships-limited" aria-label={t.name}>
              <div className="mships-card-top">
                <h3 className="mships-card-title">{t.name}</h3>
                <span className="mships-chip mships-chip--limited">Limited</span>
              </div>
              <p className="mships-price">
                <span className="mships-figure mships-figure--s">{t.price}</span> <span className="mships-per">{t.per}</span>
              </p>
              <p className="mships-blurb">{`${t.summary}. ${t.blurb}`}</p>
              <Button variant="neutral" disabled describedBy={SHUT_ID} onClick={() => undefined}>
                {`Choose ${t.name}`}
              </Button>
            </article>
          ))}
          {o.seniors !== undefined ? (
            <article className="mships-card" aria-label="Senior plans">
              <p className="mships-cap">{`${String(o.seniors.age)} and over`}</p>
              <h3 className="mships-card-title">Senior plans</h3>
              <ul className="mships-rows">
                {o.seniors.rows.map((r) => (
                  <li key={r.name}>
                    <span>{r.name}</span>
                    <span className="mships-row-price">{r.price}</span>
                  </li>
                ))}
              </ul>
            </article>
          ) : null}
          {o.addOns !== undefined ? (
            <article className="mships-card" aria-label="Add-ons">
              <p className="mships-cap">{o.addOns.universal ? "Add to any plan" : "Add to a plan"}</p>
              <h3 className="mships-card-title">Add-ons</h3>
              <ul className="mships-rows">
                {o.addOns.rows.map((r) => (
                  <li key={r.name}>
                    <span>{r.name}</span>
                    <span className="mships-row-price">{r.price}</span>
                  </li>
                ))}
              </ul>
            </article>
          ) : null}
        </section>
      ) : null}
      <section className="mships-foot" aria-label="What every plan includes, and before you buy">
        {o.includes.length > 0 ? (
          <article className="mships-card mships-includes">
            <h2 className="mships-section-cap">Every plan includes</h2>
            <Lines lines={o.includes.map((text) => ({ text, has: true }))} />
          </article>
        ) : null}
        <article className="mships-card mships-policies">
          <h2 className="mships-section-cap">Before you buy</h2>
          {o.beforeYouBuy.map((p) => (
            <p key={p} className="mships-policy">
              {p}
            </p>
          ))}
        </article>
      </section>
    </>
  );
}
